import { useState } from 'react';
import { LocationEventsCalendar } from './LocationEventsCalendar.jsx';
import { AvailabilityChangesInbox } from './AvailabilityChangesInbox.jsx';
import { MyScheduleView } from './MyScheduleView.jsx';
import { TimeOffView } from './TimeOffView.jsx';
import { ShiftSwapsView } from './ShiftSwapsView.jsx';
import { MyTimeCardView } from './MyTimeCardView.jsx';
import { BuildScheduleView } from './BuildScheduleView.jsx';
import { AvailabilityHealthView } from './AvailabilityHealthView.jsx';
import { ReportCalloutModal } from './ReportCalloutModal.jsx';

export function ScheduleSection({ activeTab, setActiveTab, location, locations, user }) {
  // Schedule top-level click + the legacy 'sched-week' id both land on
  // My Schedule (the new default). Sub-tabs:
  //   My Schedule (default, with view toggles inside)
  //   Shift swaps (still a Phase-2 stub)
  //   My Time Card (renamed Timesheets)
  const isManager = window.EmpireRoles.isManager(user);
  const sub = (activeTab === 'schedule' || activeTab === 'sched-week')
    ? 'sched-mine' : activeTab;
  const [showEvents, setShowEvents] = useState(false);
  // Sick-call coverage: quiet by default — one button, the panel only
  // appears when a manager is acting on a call-out (spec's placement call).
  const [showCallout, setShowCallout] = useState(false);
  // CJ feedback 2026-05-09: My Availability + Time Off moved to the
  // Profile (PTO balance + request flow lives there). Schedule stays
  // focused on shifts, swaps, time card, and (for managers) the ops
  // and build views.
  const tabs = [
    { id: 'sched-mine',         label: 'My Schedule' },
    { id: 'sched-swaps',        label: 'Shift swaps' },
    { id: 'sched-time',         label: 'Time Card' },
    ...(isManager ? [{ id: 'sched-timeoff', label: 'Time Off Approvals' }] : []),
    ...(isManager ? [{ id: 'sched-ops', label: 'Availability Health' }] : []),
    ...(isManager ? [{ id: 'sched-build', label: 'Build Schedule' }] : []),
  ];
  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {isManager && (
          <button onClick={() => setShowCallout(true)}
            style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600,
                      border: '1px solid var(--gold)', background: '#fff', color: '#212121',
                      borderRadius: 8, cursor: 'pointer' }}>
            🤒 Report call-out
          </button>
        )}
        <button onClick={() => setShowEvents(true)}
          style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600,
                    border: '1px solid var(--gold)', background: '#fff', color: '#212121',
                    borderRadius: 8, cursor: 'pointer' }}>
          📅 Events
        </button>
      </div>
      {showCallout && (
        <ReportCalloutModal currentUser={user} onClose={() => setShowCallout(false)} />
      )}
      {showEvents && (
        <LocationEventsCalendar
          locationId={location?.id}
          locationName={location?.name}
          onClose={() => setShowEvents(false)} />
      )}
      {/* CJ 2026-08-02: the availability-changes inbox used to render here,
          above the sub-tabs, so it appeared on EVERY schedule tab —
          including Availability Health, where he didn't want it. It is an
          approval queue, so it now lives with the other approval queue,
          inside Time Off Approvals. */}
      {/* hide-on-mobile — the universal mobile sub-tab strip in the page
          header already covers the Schedule children. Showing them
          twice on mobile is what CJ caught. */}
      <div className="schedule-subtabs-desktop" style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e0e0e0', paddingBottom: 0, overflowX: 'auto' }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              padding: '10px 16px', fontSize: 13, fontWeight: 600,
              background: 'transparent', border: 'none',
              borderBottom: `2px solid ${sub === t.id ? 'var(--forest)' : 'transparent'}`,
              color: sub === t.id ? '#212121' : '#757575',
              cursor: 'pointer', fontFamily: 'inherit',
              marginBottom: -1, whiteSpace: 'nowrap',
            }}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'sched-mine' && <MyScheduleView location={location} locations={locations} user={user} isManager={isManager} />}
      {sub === 'sched-timeoff' && (
        <div className="space-y-4">
          {/* Both manager approval queues in one place (CJ 2026-08-02):
              time off to decide, and availability changes to acknowledge.
              Changes sit above the queue because they are usually the
              shorter list and the one that goes stale unnoticed. */}
          {isManager && <AvailabilityChangesInbox currentUser={user} />}
          <TimeOffView currentUser={user} isManager={isManager} initialScope="queue" />
        </div>
      )}
      {sub === 'sched-swaps' && (
        <ShiftSwapsView currentUser={user} isManager={isManager} />
      )}
      {sub === 'sched-time' && <MyTimeCardView location={location} user={user} isManager={isManager} />}
      {sub === 'sched-build' && isManager && (
        <BuildScheduleView location={location} user={user} />
      )}
      {sub === 'sched-ops' && isManager && (
        // CJ 2026-06-30: Availability Health follows the location
        // dropdown — managers want to see the café they're standing in,
        // not a chain-wide rollup. The 56-day cross-location map is
        // still available via the "All locations" toggle inside.
        <AvailabilityHealthView user={user} location={location} />
      )}
    </div>
  );
}
